import { object, string, number, array, ValidationError, boolean } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { phoneNumber } from '~/ui/constants/validation';
import { isDateBigger } from '~/utils/date';

const validationSchema = object()
  .shape({
    programId: number().nullable().required('Field is required'),
    healthCareManagerIds: array().of(number()).min(1, 'Field is required'),
    subjectId: string().max(50, 'Max length - 50 symbols').required('Field is required'),
    firstName: string().max(100, 'Max length - 100 symbols').required('Field is required'),
    middleName: string().max(100, 'Max length - 100 symbols'),
    lastName: string().max(100, 'Max length - 100 symbols').required('Field is required'),
    phoneNumber: phoneNumber.required('Field is required'),
    dateOfBirth: string().required('Field is required'),
    ethnicity: string().nullable(),
    dialingNote: string().nullable().max(30),
    isHomeHealthCare: string().nullable(),
    gender: string().nullable(),
    country: string().nullable().required('Field is required'),
    state: string().nullable().required('Field is required'),
    city: string().max(100, 'Max length - 100 symbols').required('Field is required'),
    address: string().max(100, 'Max length - 100 symbols').required('Field is required'),
    zip: string().max(20, 'Max length - 20 symbols').required('Field is required'),
    bloodPressureDeviceId: number().nullable(),
    weightScaleDeviceId: number().nullable(),
    pulseOximeterDeviceId: number().nullable(),
    bloodGlucoseDeviceId: number().nullable(),
    comment: string().max(500, 'Max length - 500 symbols'),
    caretakers: array().of(
      object().shape({
        phone: phoneNumber.required('Field is required'),
      }),
    ),
    homeHealthcarePeople: array().of(
      object().shape({
        phone: phoneNumber.required('Field is required'),
      }),
    ),
    storageFiles: array().of(
      object().shape({
        id: number().required(),
        documentTypeId: number().nullable().required('Field is required'),
      }),
    ),
    insurance: object().shape({
      nameId: number().nullable().required('Field is required'),
      groupId: string().max(100, 'Max length - 100 symbols'),
      policyNumber: string().max(100, 'Max length - 100 symbols'),
      type: string().required('Field is required'),
      plan: string().nullable(),
      planType: string().nullable(),
      expiresAt: string().nullable(),
      isAuthorizationRequired: boolean(),
      authorizationStartDate: string()
        .nullable()
        .when('isAuthorizationRequired', {
          is: true,
          then: string().nullable().required('Field is required'),
        }),
      authorizationEndDate: string()
        .nullable()
        .when('isAuthorizationRequired', {
          is: true,
          then: string()
            .nullable()
            .required('Field is required')
            .test({
              name: 'autorizationDateCompare',
              message: '`Authorization Valid Through` should be bigger then `Authorization From`',
              test: (value, context) => {
                const { authorizationStartDate } = context.parent;

                return (
                  !authorizationStartDate || !value || isDateBigger(authorizationStartDate, value)
                );
              },
            }),
        }),
    }),
  })
  .test({
    name: 'uniquePhone',
    test: (value, { createError }) => {
      const errors = [] as ValidationError[];

      value.caretakers.forEach((item, index) => {
        if (value.caretakers.filter(x => x.phone === item.phone).length >= 2) {
          errors.push(
            new ValidationError(`'Phone' must be unique`, value, `caretakers[${index}].phone`),
          );
        }
      });

      value.homeHealthcarePeople.forEach((item, index) => {
        if (value.homeHealthcarePeople.filter(x => x.phone === item.phone).length >= 2) {
          errors.push(
            new ValidationError(
              `'Phone' must be unique`,
              value,
              `homeHealthcarePeople[${index}].phone`,
            ),
          );
        }
      });

      value.caretakers.forEach((item, index) => {
        if (value.homeHealthcarePeople.some(x => x.phone === item.phone)) {
          errors.push(
            new ValidationError(`'Phone' must be unique`, value, `caretakers[${index}].phone`),
          );
        }
      });

      value.homeHealthcarePeople.forEach((item, index) => {
        if (value.caretakers.some(x => x.phone === item.phone)) {
          errors.push(
            new ValidationError(
              `'Phone' must be unique`,
              value,
              `homeHealthcarePeople[${index}].phone`,
            ),
          );
        }
      });

      return errors.length === 0 ? true : createError({ message: () => errors });
    },
  });

export default yupResolver(validationSchema);
